import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Users as UsersIcon, X } from 'lucide-react'
import {
  groupsService,
  type OrgGroup,
  type OrgGroupMember,
} from '../../services/permissions'
import { usersService, type User } from '../../services/users'
import type { ApiErrorLike } from '../../utils/apiError'

/**
 * Admin → Groups (A6.3).
 *
 * Groups are first-class principals — an ACL grant can target "Site PMs" or
 * "Senior Associates" instead of every user individually. This page is the
 * sole place to create / rename / delete groups and manage their membership.
 */
export function GroupsSection() {
  const [groups, setGroups] = useState<OrgGroup[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<OrgGroup | null>(null)
  const [members, setMembers] = useState<OrgGroupMember[]>([])
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newKind, setNewKind] = useState('custom')

  const fetchGroups = async () => {
    setLoading(true)
    try {
      const [g, u] = await Promise.all([groupsService.list(), usersService.list()])
      setGroups(g)
      setUsers(u)
    } catch {
      toast.error('Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGroups()
  }, [])

  const openGroup = async (g: OrgGroup) => {
    setSelected(g)
    try {
      setMembers(await groupsService.members(g.id))
    } catch {
      toast.error('Failed to load members')
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Name is required')
      return
    }
    setCreating(true)
    try {
      const g = await groupsService.create({ name: newName.trim(), kind: newKind })
      setNewName('')
      setNewKind('custom')
      await fetchGroups()
      openGroup(g)
      toast.success('Group created')
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Failed to create group')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteGroup = async (g: OrgGroup) => {
    if (!confirm(`Delete group "${g.name}"?`)) return
    try {
      await groupsService.remove(g.id)
      if (selected?.id === g.id) setSelected(null)
      await fetchGroups()
      toast.success('Group deleted')
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Failed to delete')
    }
  }

  const handleToggleMember = async (userId: number) => {
    if (!selected) return
    const memberIds = new Set(members.map((m) => m.user_id))
    if (memberIds.has(userId)) memberIds.delete(userId)
    else memberIds.add(userId)
    try {
      const updated = await groupsService.replaceMembers(selected.id, [...memberIds])
      setMembers(updated)
      await fetchGroups()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Failed to update members')
    }
  }

  const memberUserIds = new Set(members.map((m) => m.user_id))

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[#102a43]">Groups</h2>
        <p className="text-sm text-[#486581] mt-1">
          Groups are reusable principals for permission grants — e.g. "Site PMs",
          "Senior Associates". Assign a role to a group on a portfolio, program,
          or project and every member inherits the access.
        </p>
      </header>

      <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[#102a43]">Create group</h3>
        <div className="flex flex-wrap gap-2">
          <input
            className="border border-[#bcccdc] rounded px-2 py-1.5 text-sm flex-1 min-w-[180px]"
            placeholder="Group name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <select
            className="border border-[#bcccdc] rounded px-2 py-1.5 text-sm"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value)}
          >
            <option value="custom">Custom</option>
            <option value="role">Role</option>
            <option value="team">Team</option>
          </select>
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-[#102a43] text-white disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="bg-white rounded-lg border-2 border-[#bcccdc]">
          <header className="px-4 py-2 border-b border-[#bcccdc]">
            <h3 className="text-sm font-semibold text-[#102a43]">All groups</h3>
          </header>
          {loading ? (
            <p className="p-4 text-sm text-[#627d98]">Loading…</p>
          ) : groups.length === 0 ? (
            <p className="p-4 text-sm text-[#627d98]">No groups yet.</p>
          ) : (
            <ul className="divide-y divide-[#bcccdc]">
              {groups.map((g) => (
                <li
                  key={g.id}
                  className={`flex items-center justify-between px-4 py-2 text-sm cursor-pointer hover:bg-[#f0f4f8] ${
                    selected?.id === g.id ? 'bg-[#f0f4f8]' : ''
                  }`}
                  onClick={() => openGroup(g)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <UsersIcon className="w-4 h-4 text-[#486581] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-[#102a43] truncate">{g.name}</div>
                      <div className="text-xs text-[#627d98]">
                        {g.kind} · {g.member_count} {g.member_count === 1 ? 'member' : 'members'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-[#9b2c2c] hover:underline ml-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteGroup(g)
                    }}
                    aria-label="Delete group"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white rounded-lg border-2 border-[#bcccdc] lg:col-span-2">
          <header className="px-4 py-2 border-b border-[#bcccdc] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#102a43]">
              {selected ? `Members of "${selected.name}"` : 'Members'}
            </h3>
            {selected && (
              <button
                type="button"
                className="text-[#627d98] hover:text-[#102a43]"
                onClick={() => setSelected(null)}
                aria-label="Close member panel"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </header>
          {!selected ? (
            <p className="p-4 text-sm text-[#627d98]">
              Choose a group on the left to view and edit its members.
            </p>
          ) : (
            <ul className="divide-y divide-[#bcccdc] max-h-[480px] overflow-y-auto">
              {users.map((u) => {
                const isMember = memberUserIds.has(u.id)
                return (
                  <li
                    key={u.id}
                    className="flex items-center justify-between px-4 py-2 text-sm"
                  >
                    <div>
                      <div className="text-[#102a43]">{u.full_name || u.email}</div>
                      {u.full_name && (
                        <div className="text-xs text-[#627d98]">{u.email}</div>
                      )}
                    </div>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isMember}
                        onChange={() => handleToggleMember(u.id)}
                      />
                      <span className="text-xs text-[#486581]">
                        {isMember ? 'Member' : 'Add'}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
